<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'
import Card from '@uiKit/components/Card.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import { routes } from '@app/router/routes'
import PropertyCard from '../components/list/PropertyCard.vue'
import { usePropertiesQuery } from '../api/usePropertiesQuery'

// Smart container for the Property Portfolio ledger: owns the infinite query,
// maps pages → flat rows, and routes to detail / the create wizard.
//
// The empty state routes to the listing-request wizard rather than to anything
// property-shaped: a seller cannot create a property, only a sell request that an
// admin later promotes.
const router = useRouter()

const query = usePropertiesQuery()

const rows = computed(() => query.data.value?.pages.flatMap((page) => page.rows) ?? [])
const isFirstLoad = computed(() => query.isPending.value)
const isEmpty = computed(
  () => !query.isPending.value && !query.isError.value && rows.value.length === 0,
)
const errorMessage = computed(() => (query.isError.value ? 'Could not load your properties.' : ''))

const SKELETON_ROWS = 3

function goToDetail(id: string) {
  void router.push(routes.PROPERTY_DETAIL.replace(':id', id))
}

function goToNew() {
  void router.push(routes.LISTING_NEW)
}

function loadMore() {
  if (query.hasNextPage.value && !query.isFetchingNextPage.value) void query.fetchNextPage()
}
</script>

<template>
  <Card variant="outlined" padding="6" class="properties">
    <Box flex align="center" justify="between" gap="3" class="properties__head">
      <Box direction="col" gap="1">
        <Text is="h2" type="title-lg" weight="bold">Property Portfolio</Text>
        <Text type="caption" color="muted">
          Detailed ledger of your listed assets, funding progress, and document status.
        </Text>
      </Box>
      <Button label="List New Property" color="primary" icon="plus" @click="goToNew" />
    </Box>

    <Message v-if="errorMessage" color="danger" variant="outlined" class="properties__message">
      {{ errorMessage }}
    </Message>

    <Box v-if="isFirstLoad" direction="col" gap="3" class="properties__body">
      <Skeleton v-for="index in SKELETON_ROWS" :key="index" height="5.5rem" />
    </Box>

    <EmptyState
      v-else-if="isEmpty"
      title="No properties yet"
      description="Once a sell request is approved, signed and promoted by our team, the property appears here."
      icon="building"
      primary-action-label="List New Property"
      primary-action-icon="pi pi-plus"
      @primary-action="goToNew"
    />

    <Box v-else direction="col" gap="3" class="properties__body">
      <PropertyCard
        v-for="property in rows"
        :key="property.id"
        :property="property"
        @view="goToDetail"
      />

      <Box v-if="query.hasNextPage.value" flex justify="center" class="properties__more">
        <Button
          label="Load more"
          variant="outline"
          :loading="query.isFetchingNextPage.value"
          @click="loadMore"
        />
      </Box>
    </Box>
  </Card>
</template>

<style scoped>
.properties__head {
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.properties__message {
  margin-bottom: 1rem;
}

.properties__more {
  margin-top: 0.5rem;
}
</style>
